package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/** Sets raw CSS properties that Kobweb has no typed modifier for (clamp(), grid templates, font-stretch...). */
fun Modifier.css(vararg properties: Pair<String, Any>): Modifier = styleModifier {
    properties.forEach { (name, value) -> property(name, value.toString()) }
}

/** Root of every redesigned page: colours, body font and a few element defaults. */
val RdPageStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(RdColor.Ground)
            .color(RdColor.Ink)
            .fontFamily(*RdFont.BODY)
            .css("font-size" to "1.0625rem", "line-height" to 1.6, "-webkit-font-smoothing" to "antialiased", "overflow-x" to "clip")
    }
    cssRule(" *") { Modifier.css("box-sizing" to "border-box") }
    // Only links inside running text; buttons, cards and menus set their own colours.
    cssRule(" p a") { Modifier.color(org.jetbrains.compose.web.css.Color("inherit")) }
    cssRule(" p") { Modifier.margin(0.px) }
    cssRule(" ul") { Modifier.margin(0.px) }
    cssRule(" ol") { Modifier.margin(0.px) }
    cssRule(" img") { Modifier.maxWidth(100.percent) }
    // In-page links (#contact, #demo...) stop below the sticky header. :where keeps this easy to override.
    cssRule(" :where(section[id])") { Modifier.css("scroll-margin-top" to "84px") }
    // Grid and flex styles would otherwise override the hidden attribute.
    cssRule(" [hidden]") { Modifier.css("display" to "none !important") }
    // cssRule only scopes the first selector of a comma list, so each heading level gets its own rule.
    listOf(" h1", " h2", " h3", " h4").forEach { heading ->
        cssRule(heading) {
            Modifier
                .margin(0.px)
                .css("text-wrap" to "balance", "color" to "inherit")
        }
    }
    cssRule(" :focus-visible") {
        Modifier.css("outline" to "3px solid rgb(180,198,220)", "outline-offset" to "3px", "border-radius" to "4px")
    }
}

/** Centred content column with side gutters. */
val RdWrapStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(RdSize.MAX_WIDTH_PX.px)
        .css("margin-inline" to "auto", "padding-inline" to RdSize.SIDE_GUTTER)
}

val RdSectionStyle = CssStyle.base {
    Modifier.css("padding-block" to RdSize.SECTION_PADDING)
}

/** A section that follows another section with the same background. */
val RdSectionFlushTopStyle = CssStyle.base {
    Modifier.css("padding-top" to "0")
}

val RdSectionHeadStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Grid)
        .gap(14.px)
        .maxWidth(760.px)
        .css("margin-bottom" to "40px")
}

val RdEyebrowStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(.6.cssRem)
            .color(RdColor.Muted)
            .css("font" to "500 .78rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".12em", "text-transform" to "uppercase")
    }
    cssRule("::before") {
        Modifier.css("content" to "\"\"", "width" to "22px", "height" to "2px", "background" to "rgb(180,198,220)")
    }
}

val RdLedeStyle = CssStyle.base {
    Modifier
        .color(RdColor.Muted)
        .css("max-width" to "60ch", "font-size" to "1.1rem")
}

val RdH2Style = CssStyle.base {
    Modifier.fontFamily(*RdFont.DISPLAY).css(
        "letter-spacing" to "-.01em",
        "font-size" to "clamp(1.9rem, 1.2rem + 2.4vw, 2.9rem)",
        "font-weight" to 850, "font-stretch" to "112%", "line-height" to 1.05
    )
}

val RdH3Style = CssStyle.base {
    Modifier.fontFamily(*RdFont.DISPLAY).css("letter-spacing" to "-.01em", "font-size" to "1.2rem", "font-weight" to 750, "font-stretch" to "105%", "line-height" to 1.2)
}

/** Highlighter stroke behind a word in a heading. */
val RdMarkStyle = CssStyle.base {
    Modifier.css(
        "background" to "linear-gradient(transparent 58%, rgb(180,198,220) 58% 92%, transparent 92%)",
        "padding-inline" to ".06em"
    )
}

private fun buttonBase() = Modifier
    .css("display" to "inline-flex")
    .alignItems(AlignItems.Center)
    .gap(.5.cssRem)
    .borderRadius(10.px)
    .textDecorationLine(TextDecorationLine.None)
    .cursor(Cursor.Pointer)
    .border(2.px, LineStyle.Solid, org.jetbrains.compose.web.css.Color("transparent"))
    .css("font" to "600 1rem/1 ${RdFont.BODY_STACK}", "padding" to "15px 22px")
    .transition(Transition.of("transform", 150.ms), Transition.of("background", 150.ms))

val RdPrimaryButtonStyle = CssStyle {
    base { buttonBase().backgroundColor(RdColor.Brand).color(RdColor.BrandInk) }
    hover { Modifier.backgroundColor(RdColor.Band).css("transform" to "translateY(-1px)") }
}

val RdGhostButtonStyle = CssStyle {
    base { buttonBase().color(RdColor.Ink).css("border-color" to "rgb(30,40,52)", "background" to "transparent") }
    hover { Modifier.css("transform" to "translateY(-1px)") }
}

/** Outline button on the dark band. */
val RdGhostButtonOnDarkStyle = CssStyle {
    base { buttonBase().color(RdColor.BandText).css("border-color" to "rgb(238,243,248)", "background" to "transparent") }
    hover { Modifier.css("transform" to "translateY(-1px)") }
}

val RdArrowStyle = CssStyle.base {
    Modifier.fontFamily(*RdFont.MONO)
}

/** Small square button that copies contact details; widens to show "Copied" after a click. */
val RdCopyButtonStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(6.px).cursor(Cursor.Pointer)
            .color(RdColor.Brand).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(8.px)
            .css("flex" to "none", "justify-content" to "center", "min-width" to "32px", "height" to "32px", "padding" to "0 8px", "font" to "600 .8rem/1 ${RdFont.BODY_STACK}")
    }
    hover { Modifier.css("border-color" to "rgb(46,75,110)") }
}

/** Links inside running text: same colour as the text, underlined, so they read as links in lists and tables too. */
val RdInlineLinkStyle = CssStyle.base {
    Modifier.css("color" to "inherit", "text-decoration" to "underline", "text-underline-offset" to "3px")
}

val RdPillStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.LogoBlue)
        .color(RdColor.Ink)
        .borderRadius(999.px)
        .css("font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".06em", "text-transform" to "uppercase", "padding" to "6px 8px")
}

/** White circle holding a line icon. */
val RdIconCircleStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.Paper)
        .color(RdColor.Brand)
        .borderRadius(50.percent)
        .css("flex" to "none", "width" to "44px", "height" to "44px", "padding" to "10px", "display" to "grid", "place-items" to "center")
}
